import { useState } from 'react';

// PowerBuilder source-export object types, matched by the extensions PowerBuilder
// itself writes when a library is exported to text for source control (System Tree
// in the PB IDE shows the same objects grouped under a .pbl the same way).
const PB_TYPES = {
  pbw: { label: 'Workspace', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><rect width="16" height="16" rx="4" fill="#3B82F6"/><path d="M5.5 5L3 8l2.5 3M10.5 5L13 8l-2.5 3" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
  ) },
  pbt: { label: 'Target', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="#2563EB"/><circle cx="8" cy="8" r="4.5" fill="none" stroke="white" strokeWidth="1.2"/><circle cx="8" cy="8" r="1.6" fill="white"/></svg>
  ) },
  sra: { label: 'Application', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="#2563EB"/><circle cx="8" cy="8" r="4.5" fill="none" stroke="white" strokeWidth="1.2"/><circle cx="8" cy="8" r="1.6" fill="white"/></svg>
  ) },
  pbl: { label: 'Library', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><rect x="1" y="9" width="3.4" height="6" rx="0.6" fill="#EF4444"/><rect x="6.3" y="5" width="3.4" height="10" rx="0.6" fill="#22C55E"/><rect x="11.6" y="2" width="3.4" height="13" rx="0.6" fill="#3B82F6"/></svg>
  ) },
  srw: { label: 'Window', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><rect x="1" y="3" width="14" height="11" rx="1.5" fill="#60A5FA"/><rect x="1" y="3" width="14" height="3" rx="1.2" fill="#2563EB"/></svg>
  ) },
  srm: { label: 'Menu', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="#F59E0B"/><path d="M4.5 6h7M4.5 8.5h7M4.5 11h4.5" stroke="white" strokeWidth="1.2" strokeLinecap="round"/></svg>
  ) },
  sru: { label: 'User Object', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="#EAB308"/><circle cx="8" cy="6" r="2.3" fill="white"/><path d="M3.3 13c0.7-2.6 2.6-4 4.7-4s4 1.4 4.7 4" fill="white"/></svg>
  ) },
  srd: { label: 'DataWindow', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><rect x="1" y="1" width="6.5" height="6.5" rx="1" fill="#EF4444"/><rect x="8.5" y="1" width="6.5" height="6.5" rx="1" fill="#3B82F6"/><rect x="1" y="8.5" width="6.5" height="6.5" rx="1" fill="#3B82F6"/><rect x="8.5" y="8.5" width="6.5" height="6.5" rx="1" fill="#EF4444"/></svg>
  ) },
  srf: { label: 'Function', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><rect width="16" height="16" rx="4" fill="#8B5CF6"/><text x="8" y="11.5" textAnchor="middle" fontSize="9" fontStyle="italic" fill="white" fontFamily="serif">f</text></svg>
  ) },
  srs: { label: 'Structure', icon: (
    <svg width="14" height="14" viewBox="0 0 16 16"><rect x="2" y="2" width="12" height="3" rx="1" fill="#64748B"/><rect x="2" y="6.5" width="12" height="3" rx="1" fill="#94A3B8"/><rect x="2" y="11" width="12" height="3" rx="1" fill="#CBD5E1"/></svg>
  ) },
};

export function pbTypeFor(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  return PB_TYPES[ext];
}

export function isNodeFolder(node) {
  return node.nodeType === 'Folder' || node.type === 'folder' || node.isDirectory === true;
}

export function sortNodes(nodes) {
  return [...nodes].sort((a, b) => {
    const aFolder = isNodeFolder(a);
    const bFolder = isNodeFolder(b);
    if (aFolder !== bFolder) return aFolder ? -1 : 1;
    const aName = (a.name ?? a.fileName ?? '').toLowerCase();
    const bName = (b.name ?? b.fileName ?? '').toLowerCase();
    return aName.localeCompare(bName);
  });
}

export function hasMatch(node, q) {
  const name = (node.name ?? node.fileName ?? '').toLowerCase();
  if (name.includes(q)) return true;
  const children = node.children ?? node.files ?? node.items ?? [];
  return children.some(c => hasMatch(c, q));
}

export function highlightText(text, query) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ background: 'rgba(255, 200, 0, 0.35)', borderRadius: 2, padding: '0 1px', fontWeight: 700 }}>
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  );
}

export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function countNodes(node) {
  if (!node) return 0;
  const children = node.children ?? node.files ?? node.items ?? [];
  return 1 + children.reduce((sum, c) => sum + countNodes(c), 0);
}

// query prop: when provided externally, hides the internal search bar
export function FileTreeView({ node, query: externalQuery }) {
  const [internalRawQuery, setInternalRawQuery] = useState('');
  const controlled = externalQuery !== undefined;
  const rawQuery = controlled ? externalQuery : internalRawQuery;
  const query = rawQuery.trim().toLowerCase();

  if (!node) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '40px 0', color: 'var(--text-muted)' }}>
        <span style={{ fontSize: 32 }}>📭</span>
        <p style={{ margin: 0, fontSize: 13 }}>No files uploaded yet for this resource.</p>
      </div>
    );
  }

  const rawChildren = Array.isArray(node) ? node : (node.children ?? []);
  const topChildren = sortNodes(rawChildren);

  if (topChildren.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '40px 0', color: 'var(--text-muted)' }}>
        <span style={{ fontSize: 32 }}>📭</span>
        <p style={{ margin: 0, fontSize: 13 }}>No files found.</p>
      </div>
    );
  }

  const hasAnyMatch = !query || topChildren.some(c => hasMatch(c, query));

  return (
    <div>
      {!controlled && (
        <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-secondary, rgba(255,255,255,0.05))', borderRadius: 6, padding: '5px 10px', border: '1px solid var(--border)' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>🔍</span>
          <input
            type="text"
            value={internalRawQuery}
            onChange={e => setInternalRawQuery(e.target.value)}
            placeholder="Filter files and folders…"
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 12, color: 'var(--text-primary)', flex: 1, fontFamily: 'var(--font-mono, monospace)' }}
          />
          {internalRawQuery && (
            <button onClick={() => setInternalRawQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
          )}
        </div>
      )}

      {!hasAnyMatch && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 12 }}>
          No files or folders match <strong>"{rawQuery}"</strong>
        </div>
      )}

      {hasAnyMatch && (
        <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>
          {topChildren.map((child, i) => (
            <FileNode key={child.name ?? i} node={child} depth={0} query={query} />
          ))}
        </div>
      )}
    </div>
  );
}

export function FileNode({ node, depth, query }) {
  const [openState, setOpenState] = useState(true);
  const name = node.name ?? node.fileName ?? node.resourceText ?? '?';
  const rawChildren = node.children ?? node.files ?? node.items ?? [];
  const children = sortNodes(rawChildren);
  const isFolder = isNodeFolder(node);

  const selfMatches = query ? name.toLowerCase().includes(query) : true;
  const childMatches = query && isFolder && children.some(c => hasMatch(c, query));

  if (query && !selfMatches && !childMatches) return null;

  const open = query ? (selfMatches || childMatches) : openState;

  const ext = name.split('.').pop().toLowerCase();
  const fileIconMap = {
    js: '🟨', ts: '🟦', tsx: '🟦', jsx: '🟨', json: '📋',
    html: '🌐', css: '🎨', scss: '🎨', md: '📝', txt: '📄',
    xml: '📄', yaml: '📄', yml: '📄',
    png: '🖼️', jpg: '🖼️', jpeg: '🖼️', gif: '🖼️', svg: '🖼️',
    zip: '🗜️', pdf: '📕', sql: '🗄️', py: '🐍', java: '☕',
  };
  const pbType = pbTypeFor(name);
  // A .pbl export is a folder of objects in this app's tree, not a leaf file —
  // still show the Library icon instead of the generic folder glyph.
  const icon = pbType
    ? pbType.icon
    : isFolder ? (open ? '📂' : '📁') : (fileIconMap[ext] ?? '📄');
  const iconTitle = pbType?.label;

  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '3px 8px', paddingLeft: 8 + depth * 16,
          cursor: isFolder ? 'pointer' : 'default',
          borderRadius: 4,
          background: selfMatches && query ? 'rgba(255, 200, 0, 0.07)' : undefined,
        }}
        onClick={() => isFolder && !query && setOpenState(o => !o)}
      >
        {isFolder && <span style={{ fontSize: 9, color: 'var(--text-muted)', width: 10 }}>{open ? '▾' : '▸'}</span>}
        {!isFolder && <span style={{ width: 10 }} />}
        <span style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center' }} title={iconTitle}>{icon}</span>
        <span style={{ color: isFolder ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: isFolder ? 600 : 400 }}>
          {highlightText(name, query)}
        </span>
        {node.size != null && (
          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)' }}>
            {formatFileSize(node.size)}
          </span>
        )}
      </div>
      {isFolder && open && children.map((child, i) => (
        <FileNode key={child.name ?? child.fileName ?? i} node={child} depth={depth + 1} query={query} />
      ))}
    </div>
  );
}
